import type { Dispatch } from "redux";
import type { BigSearchPersonEvent } from "../../api/big-search";
import {
  FETCH_CONTRACTOR_SHOWS_REQUEST,
  FETCH_CONTRACTOR_SHOWS_SUCCESS,
  FETCH_CONTRACTOR_SHOWS_FAILURE,
} from "../constants/contractorShows";
import { fetchBigSearchPersonEvents } from "../../api/big-search";

/**
 * How many shows to pull in one go.
 *
 * NOT a display limit — the card shows 5 past and 5 upcoming, and the split
 * happens on the client (`utils/methods/contractorShowHistory`). Asking for 5
 * would return the five furthest-future bookings and leave the past bucket
 * permanently empty, because the endpoint sorts by start date DESC.
 *
 * 100 is the endpoint's ceiling (`parsePagination`), and it costs nothing extra:
 * the aggregation already builds the person's full event list and paginates it
 * in memory, so a smaller page would do the same database work for less data.
 * The only case this truncates is somebody with 95+ future bookings, which does
 * not happen to crew.
 */
export const CONTRACTOR_SHOWS_FETCH_LIMIT = 100;

/**
 * Shows a contractor was scheduled on, for the contact card.
 *
 * `involvement: "worked"` on purpose: the endpoint also reports events the
 * person COORDINATED, and this block answers "shows worked". Mixing the two
 * would credit a coordinator with crew work they never did.
 *
 * ⚠️ Caller must gate by role before dispatching — the whole `big-search`
 * controller is `@Roles('Admin', 'Super Admin')`, so anyone else gets a 403.
 */
export const fetchContractorShowsAction =
  ({
    contractorId,
    successCallback,
    failureCallback,
  }: {
    contractorId: string;
    successCallback?: (results: BigSearchPersonEvent[]) => void;
    failureCallback?: (error: unknown) => void;
  }) =>
  async (dispatch: Dispatch) => {
    dispatch({
      type: FETCH_CONTRACTOR_SHOWS_REQUEST,
      payload: { contractorId },
    });

    const { response, error } = await fetchBigSearchPersonEvents(contractorId, {
      page: 1,
      limit: CONTRACTOR_SHOWS_FETCH_LIMIT,
      involvement: "worked",
    });

    if (response?.data) {
      const body = response.data?.data ?? response.data;
      const results = Array.isArray(body?.results) ? body.results : [];
      dispatch({
        type: FETCH_CONTRACTOR_SHOWS_SUCCESS,
        payload: {
          contractorId,
          data: { results, totalCount: body?.totalCount ?? results.length },
        },
      });
      successCallback?.(results);
    } else {
      // The entry is still written, as an empty result. Without it the "already
      // fetched?" guard in the card never flips and every hover retries a call
      // that is failing for a structural reason (403, deleted person).
      dispatch({
        type: FETCH_CONTRACTOR_SHOWS_FAILURE,
        payload: { contractorId, error },
      });
      failureCallback?.(error);
    }
  };
